import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Input } from "@/components/ui/input";
import { useState } from "react";

interface NotificationSnoozeDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (durationMinutes: number) => void;
  notificationCount?: number;
}

const SNOOZE_OPTIONS = [
  { label: "30 minutes", value: 30 },
  { label: "1 hour", value: 60 },
  { label: "2 hours", value: 120 },
  { label: "4 hours", value: 240 },
  { label: "Tomorrow morning (9 AM)", value: -1 },
  { label: "Custom", value: 0 },
];

export function NotificationSnoozeDialog({
  open,
  onOpenChange,
  onConfirm,
  notificationCount = 1,
}: NotificationSnoozeDialogProps) {
  const [selectedOption, setSelectedOption] = useState("60");
  const [customMinutes, setCustomMinutes] = useState("");

  const handleConfirm = () => {
    const duration = parseInt(selectedOption);
    if (duration === 0) {
      onConfirm(parseInt(customMinutes) || 60);
    } else if (duration === -1) {
      const now = new Date();
      const tomorrow9AM = new Date();
      tomorrow9AM.setDate(tomorrow9AM.getDate() + 1);
      tomorrow9AM.setHours(9, 0, 0, 0);
      const minutes = Math.floor((tomorrow9AM.getTime() - now.getTime()) / 60000);
      onConfirm(minutes);
    } else {
      onConfirm(duration);
    }
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Snooze Notification{notificationCount > 1 ? "s" : ""}</DialogTitle>
          <DialogDescription>
            {notificationCount > 1
              ? `Snooze ${notificationCount} notifications. They'll reappear after the selected time.`
              : "Choose when to be reminded about this notification."}
          </DialogDescription>
        </DialogHeader>

        <RadioGroup value={selectedOption} onValueChange={setSelectedOption}>
          {SNOOZE_OPTIONS.map((option) => (
            <div key={option.value} className="flex items-center space-x-2">
              <RadioGroupItem value={option.value.toString()} id={`option-${option.value}`} />
              <Label htmlFor={`option-${option.value}`} className="cursor-pointer">
                {option.label}
              </Label>
            </div>
          ))}
        </RadioGroup>

        {selectedOption === "0" && (
          <div className="space-y-2 pt-2">
            <Label htmlFor="custom">Custom duration (minutes)</Label>
            <Input
              id="custom"
              type="number"
              min="1"
              placeholder="Enter minutes"
              value={customMinutes}
              onChange={(e) => setCustomMinutes(e.target.value)}
            />
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleConfirm}>Snooze</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
